import React, { useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Dimensions, Image, Platform, LayoutChangeEvent } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { IBulletinTableRow } from '@/types/admin/bulletins';
import { 
  formatBulletinDate, 
  getRecipientTypeLabel
} from '@/utils/admin/bulletinUtils';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import BulletinImagesCarousel from '@/components/common/bulletins/BulletinImagesCarousel';
import BulletinDescription from '@/components/common/bulletins/BulletinDescription';
import { useRemoteImageAspect } from '@/hooks/common/useRemoteImageAspect';

const { width: screenWidth } = Dimensions.get('window');

// Hoisted constants to avoid per-render allocations
const GRADIENT_COLORS = [
  ADMIN_COLORS.BULLETINS.GRADIENT_BLUE,
  ADMIN_COLORS.BULLETINS.GRADIENT_PURPLE,
  ADMIN_COLORS.BULLETINS.GRADIENT_RED,
] as const;
const GRADIENT_LOCATIONS = [0.1138, 0.6385, 0.9773] as const;
const SNAP_GAP = 0;

interface BulletinCardProps {
  bulletin: IBulletinTableRow;
  onEdit?: (bulletin: IBulletinTableRow) => void;
  isExpanded?: boolean;
  onToggleExpanded?: (bulletinId: number) => void;
  feedFilter?: string; // Add feed filter context to determine draft status
  variant?: 'admin' | 'resident';
}

const BulletinCardBase: React.FC<BulletinCardProps> = ({
  bulletin,
  onEdit,
  isExpanded = false,
  onToggleExpanded,
  feedFilter,
  variant = 'admin',
}) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageContainerWidth, setImageContainerWidth] = useState<number>(0);
  const [imageAspect, setImageAspect] = useState<number | null>(null); // height / width
  // removed unused priority computation

  // Get description from bulletin data (matching admin-web v2)
  const description = bulletin.description || '';
  const shouldTruncate = description.length > 150;
  const displayDescription = shouldTruncate && !isExpanded 
    ? `${description.slice(0, 150)}...` 
    : description;

  // Get author info (matching admin-web v2)
  const author = bulletin.author;
  const authorName = author?.firstName 
    ? `${author.firstName} ${author.lastName || ''}`.trim()
    : author?.email;

  // Get files/images (matching admin-web v2)
  const files = bulletin.files || [];
  const hasImages = files && files.length > 0;
  const imageCount = files.length;

  // Check if bulletin is a draft or scheduled (matching admin-web v2 logic)
  // Use feed filter context instead of client-side logic
  const isDraft = feedFilter === 'Draft';
  const isScheduled = feedFilter === 'Scheduled';
  const isEditable = isDraft || isScheduled;
  const isResidentVariant = variant === 'resident';

  // Gradient colors from GradientBorderFrame (hoisted above)

  // Measure actual image aspect ratio using shared hook (mirror Events Info)
  const measuredAspect = useRemoteImageAspect(files?.[currentImageIndex]?.fileUrl || null);
  React.useEffect(() => {
    setImageAspect(measuredAspect);
  }, [measuredAspect]);

  const onImageContainerLayout = (e: LayoutChangeEvent) => {
    setImageContainerWidth(e.nativeEvent.layout.width);
  };

  const minImageHeight = 200;
  const maxImageHeight = Platform.OS === 'web' ? 360 : 300;
  const computeHeight = (effectiveWidth: number) => {
    return (imageAspect && effectiveWidth)
      ? Math.min(maxImageHeight, Math.max(minImageHeight, Math.round(effectiveWidth * imageAspect)))
      : minImageHeight;
  };

  const scrollToImage = (index: number) => {
    if (scrollViewRef.current) {
      const containerW = imageContainerWidth || (screenWidth - 64);
      const frameW = containerW; // Ensure full-width slides for all cases
      const scrollToX = index * (frameW + SNAP_GAP);
      scrollViewRef.current.scrollTo({ x: scrollToX, animated: true });
      setCurrentImageIndex(index);
    }
  };

  const scrollToNext = () => {
    if (currentImageIndex < imageCount - 1) {
      scrollToImage(currentImageIndex + 1);
    }
  };

  const scrollToPrevious = () => {
    if (currentImageIndex > 0) {
      scrollToImage(currentImageIndex - 1);
    }
  };

  // Helper to strip stray text nodes (RN Web): mirrors FormSection pattern
  const sanitizeChildren = (children: React.ReactNode[]) => {
    return React.Children.toArray(children).filter((child) => {
      if (typeof child === 'string') {
        return child.trim() !== '';
      }
      return child !== null && child !== undefined;
    });
  };

  // Prebuild sections to sanitize before injecting into Views
  const renderHeaderDateText = () => {
    if (isResidentVariant) {
      const baseDate = bulletin.publishDate ? new Date(bulletin.publishDate) : bulletin.date;
      return baseDate ? formatBulletinDate(baseDate) : '';
    }
    if (isDraft) return '';
    if (isScheduled) {
      const iso = bulletin.publishDate || undefined;
      if (iso) {
        const d = new Date(iso);
        const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        return `Scheduled at ${datePart} ${timePart}`;
      }
      return 'Scheduled';
    }
    return formatBulletinDate(bulletin.date);
  };
  const headerElement = (
    <BulletinHeader
      variant={variant}
      isDraft={isDraft}
      isScheduled={isScheduled}
      renderHeaderDateText={renderHeaderDateText}
      bulletin={bulletin}
      isEditable={isEditable}
      onEdit={onEdit}
    />
  );

  const titleElement = (
    <Text style={styles.title}>
      {bulletin.title || bulletin.subject || 'Untitled'}
    </Text>
  );

  const imagesElement = hasImages ? (
    <BulletinImagesCarousel
      files={files}
      computeHeight={computeHeight}
      maxImageHeight={maxImageHeight}
    />
  ) : null;

  const descriptionElement = description ? (
    <BulletinDescription
      text={description}
      expanded={isExpanded}
      onToggle={() => onToggleExpanded && onToggleExpanded(bulletin.id)}
    />
  ) : null;

  const authorElement = authorName && !isResidentVariant ? (
    <Text style={styles.authorText}>
      {`Author - ${authorName}`}
    </Text>
  ) : null;

  const contentChildren = sanitizeChildren([
    headerElement,
    titleElement,
    imagesElement,
    descriptionElement,
    authorElement,
  ]);

  return (
    <TouchableOpacity
      style={styles.touchableContainer}
      activeOpacity={0.7}
    >
      <View style={styles.cardContainer}>
        {/* Gradient Border - ONLY for highlighted bulletins */}
        {bulletin.highlight && (
          <LinearGradient
            colors={[GRADIENT_COLORS[0], GRADIENT_COLORS[1], GRADIENT_COLORS[2]]}
            locations={[GRADIENT_LOCATIONS[0], GRADIENT_LOCATIONS[1], GRADIENT_LOCATIONS[2]]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.gradientBorder}
          />
        )}
        
        {/* Content Container */}
        <View style={[
          styles.contentContainer,
          bulletin.highlight && styles.contentContainerWithBorder
        ]}>
          {contentChildren.map((child, index) => {
            if (React.isValidElement(child)) {
              const existingKey = (child as any).key;
              return React.cloneElement(child as React.ReactElement, {
                key: existingKey ?? `content-${index}`,
              } as any);
            }
            return (
              <React.Fragment key={`content-${index}`}>
                {child}
              </React.Fragment>
            );
          })}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const areEqual = (prev: BulletinCardProps, next: BulletinCardProps) => {
  const a = prev.bulletin;
  const b = next.bulletin;
  if (a.id !== b.id) return false;
  if (prev.isExpanded !== next.isExpanded) return false;
  if (prev.feedFilter !== next.feedFilter) return false;
  if (prev.variant !== next.variant) return false;
  if (a.title !== b.title) return false;
  if (a.subject !== b.subject) return false;
  if ((a.description || '') !== (b.description || '')) return false;
  if (a.highlight !== b.highlight) return false;
  if (a.pinHomepage !== b.pinHomepage) return false;
  if ((a.publishDate || null) !== (b.publishDate || null)) return false;
  if (a.messageReadCount !== b.messageReadCount) return false;
  if ((a.recipientType || '') !== (b.recipientType || '')) return false;
  const aFilesLen = (a.files && a.files.length) || 0;
  const bFilesLen = (b.files && b.files.length) || 0;
  if (aFilesLen !== bFilesLen) return false;
  // Assume handler refs are stable from callers; ignore onPress/onEdit/onToggleExpanded identity
  return true;
};

export const BulletinCard = React.memo(BulletinCardBase, areEqual);

type BulletinHeaderProps = {
  variant: 'admin' | 'resident';
  isDraft: boolean;
  isScheduled: boolean;
  renderHeaderDateText: () => string;
  bulletin: IBulletinTableRow;
  isEditable: boolean;
  onEdit?: (b: IBulletinTableRow) => void;
};

const BulletinHeader: React.FC<BulletinHeaderProps> = ({
  variant,
  isDraft,
  isScheduled,
  renderHeaderDateText,
  bulletin,
  isEditable,
  onEdit,
}) => {
  const isResidentVariant = variant === 'resident';
  return (
    <View style={styles.header}>
      <View style={styles.headerTop}>
        <View style={styles.leftSection}>
          {isResidentVariant ? (
            renderHeaderDateText() ? (
              <Text style={styles.dateText}>{renderHeaderDateText()}</Text>
            ) : null
          ) : (
            <>
              {!isDraft && (
                <Text style={styles.dateText}>{renderHeaderDateText()}</Text>
              )}
              {isDraft && (
                <View style={styles.draftBadge}>
                  <Text style={styles.draftBadgeText}>DRAFT</Text>
                </View>
              )}
              {isScheduled && (
                <View style={styles.scheduledBadge}>
                  <Text style={styles.scheduledBadgeText}>SCHEDULED</Text>
                </View>
              )}
            </>
          )}
        </View>
        <View style={styles.rightSection}>
          {isResidentVariant ? (
            <View style={styles.badgesContainer}>
              {bulletin.pinHomepage && (
                <View style={styles.pinBadge}>
                  <SVG_COMPONENTS.PROPERTY width={12} height={12} color={ADMIN_COLORS.TEXT_PRIMARY} />
                </View>
              )}
            </View>
          ) : (
            <>
              <View style={styles.badgesContainer}>
                {bulletin.pinHomepage && (
                  <View style={styles.pinBadge}>
                    <SVG_COMPONENTS.PROPERTY width={12} height={12} color={ADMIN_COLORS.TEXT_PRIMARY} />
                  </View>
                )}
                <View style={styles.recipientBadge}>
                  <Text style={styles.recipientText}>
                    {getRecipientTypeLabel(bulletin.recipientType)}
                  </Text>
                </View>
                {!isScheduled && (
                  <View style={styles.readCountBadge}>
                    <SVG_COMPONENTS.EYE_OPEN width={14} height={14} color={ADMIN_COLORS.TEXT_SECONDARY} />
                    <Text style={styles.readCountText}>{bulletin.messageReadCount}</Text>
                  </View>
                )}
              </View>
              {isEditable && onEdit && (
                <TouchableOpacity
                  style={styles.editButton}
                  onPress={(e) => {
                    e.stopPropagation();
                    onEdit(bulletin);
                  }}
                  activeOpacity={0.7}
                >
                  <SVG_COMPONENTS.PENCIL width={16} height={16} color={ADMIN_COLORS.PRIMARY} />
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      </View>
    </View>
  );
};

// (Subcomponents moved to shared; local definitions removed)

const styles = StyleSheet.create({
  touchableContainer: {
    marginBottom: 16,
  },
  cardContainer: {
    position: 'relative',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  gradientBorder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 8,
  },
  contentContainer: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 8,
    padding: 16,
    // Default: no border, just background
  },
  contentContainerWithBorder: {
    // When highlighted: create gradient border effect
    borderRadius: 6, // Slightly smaller than gradient border
    margin: 2, // Creates the gradient border effect
  },
  header: {
    marginBottom: 12,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  dateText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontWeight: '400',
  },
  badgesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pinBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: 6,
  },
  recipientBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  recipientText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '500',
  },
  readCountBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  readCountText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontWeight: '500',
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    lineHeight: 28,
    marginBottom: 8,
  },
  imagesContainer: {
    marginBottom: 12,
  },
  imagesWrapper: {
    position: 'relative',
  },
  imageFrame: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    left: 8,
    transform: [{ translateY: -20 }],
    borderRadius: 20,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  navButtonRight: {
    left: 'auto',
    right: 8,
  },
  imageCounterContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 8,
  },
  imageCounter: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  imageCounterText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '500',
  },
  imagesScrollContent: {
    paddingRight: 0,
    alignItems: 'center',
  },
  imageContainer: {
    marginRight: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  singleImageContainer: {
    marginRight: 0,
  },
  bulletinImage: {
    width: screenWidth * 0.7, // 70% of screen width for multiple images
    height: 200,
    borderRadius: 8,
  },
  singleImage: {
    width: screenWidth - 64, // Full width minus padding for single image
  },
  multipleImages: {
    width: screenWidth * 0.7, // 70% of screen width for multiple images
  },
  imageIndicators: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
    gap: 6,
  },
  imageIndicatorDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  imageIndicatorDotActive: {
    backgroundColor: ADMIN_COLORS.PRIMARY,        
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  descriptionContainer: {
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    lineHeight: 20,
    marginBottom: 4,
  },
  showMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  showMoreText: {
    fontSize: 14,
    color: ADMIN_COLORS.PRIMARY,
    marginRight: 4,
  },
  showMoreIcon: {
    transform: [{ rotate: '0deg' }],
  },
  showMoreIconRotated: {
    transform: [{ rotate: '180deg' }],
  },
  authorText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontWeight: '400',
    marginTop: 8,
  },
  draftBadge: {
    backgroundColor: ADMIN_COLORS.WARNING + '20',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  draftBadgeText: {
    fontSize: 10,
    color: ADMIN_COLORS.WARNING,
    fontWeight: '600',
  },
  scheduledBadge: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '20',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  scheduledBadgeText: {
    fontSize: 10,
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '600',
  },
  editButton: {
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
